'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Typography } from '../../../components';
import { useTranslations } from '../../../contexts/translation.context';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import { FiltersSection, CostsTable, NewCostModal } from './components';
import { CostsFilters, RevshareCost } from './types';
import { RevshareCostResponse } from '../../../api/partner/partnerRevshareService';
import { DEFAULT_FILTERS, DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_DELAY } from './constants';
import partnerRevshareService from '../../../api/partner/partnerRevshareService';

// Calculate time ago from date
const getTimeAgo = (dateString: string, t: (key: string) => string, lang: string): string => {
  if (!dateString) return '';
  // Ensure the date is parsed as UTC if no timezone is specified
  const dateStr =
    dateString.endsWith('Z') || dateString.includes('+') || dateString.includes('-', 10)
      ? dateString
      : dateString + 'Z';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (lang === 'ru') {
    if (diffDays > 0) return `${diffDays}д. назад`;
    if (diffHours > 0) return `${diffHours}ч. ${diffMins % 60}мин. назад`;
    if (diffMins > 0) return `${diffMins}мин. ${diffSecs % 60}сек. назад`;
    return 'только что';
  } else {
    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ${diffMins % 60}m ago`;
    if (diffMins > 0) return `${diffMins}m ${diffSecs % 60}s ago`;
    return 'just now';
  }
};

// Transform API response to UI format
const transformCostData = (
  cost: RevshareCostResponse,
  t: (key: string) => string,
  lang: string
): RevshareCost => ({
  _id: cost._id,
  company_id: cost.company_id,
  amount: parseFloat(cost.amount) || 0,
  status: cost.status,
  created_at: cost.created_at,
  time_ago: getTimeAgo(cost.created_at, t, lang),
  description: cost.description,
  model: cost.model,
  campaign_type: 'RevShare',
  client: cost.client,
});

const RevshareCosts: React.FC = () => {
  const { t, lang } = useTranslations();

  // Breadcrumbs
  const breadcrumbs = useMemo(
    () => [
      { label: t('sidebar.dashboard') || 'Главная', href: '/partner/dashboard' },
      { label: 'Revshare', href: '/partner/revshare/offers' },
      { label: t('partner.revshare.costs.title') || 'Расходы', isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [costs, setCosts] = useState<RevshareCost[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Filters
  const [filters, setFilters] = useState<CostsFilters>(DEFAULT_FILTERS);
  const [localSearch, setLocalSearch] = useState('');

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Check if filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.campaign !== '' ||
      filters.status !== '' ||
      filters.period !== '' ||
      (filters.limit !== '' && filters.limit !== 'all')
    );
  }, [filters]);

  // Calculate date range from period filter
  const getDateRangeFromPeriod = useCallback((period: string): { start?: string; end?: string } => {
    if (!period) return {};

    const now = new Date();
    const end = now.toISOString().split('T')[0];
    let start: Date;

    switch (period) {
      case '7days':
        start = new Date(now.setDate(now.getDate() - 7));
        break;
      case '14days':
        start = new Date(now.setDate(now.getDate() - 14));
        break;
      case '30days':
        start = new Date(now.setDate(now.getDate() - 30));
        break;
      case '90days':
        start = new Date(now.setDate(now.getDate() - 90));
        break;
      default:
        return {};
    }

    return { start: start.toISOString().split('T')[0], end };
  }, []);

  // Calculate date range from limit filter
  const getDateRangeFromLimit = useCallback((limit: string): { start?: string; end?: string } => {
    if (!limit || limit === 'all') return {};

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const end = today.toISOString().split('T')[0];
    let start: Date;

    switch (limit) {
      case 'last30days':
        start = new Date(today);
        start.setDate(today.getDate() - 30);
        break;
      case 'last90days':
        start = new Date(today);
        start.setDate(today.getDate() - 90);
        break;
      case 'lastYear':
        start = new Date(today);
        start.setFullYear(today.getFullYear() - 1);
        break;
      default:
        return {};
    }

    return {
      start: start.toISOString().split('T')[0],
      end,
    };
  }, []);

  // Ref to track current request ID (to handle StrictMode double-invocation)
  const requestIdRef = useRef(0);
  // Ref to skip debounce on initial mount
  const isInitialMount = useRef(true);

  // Ref to store fetchCosts function for use in modal callback
  const fetchCostsRef = useRef<() => void>(() => {});

  // Fetch costs from API - directly in useEffect to avoid double render
  useEffect(() => {
    // Increment request ID to invalidate any in-flight requests
    const currentRequestId = ++requestIdRef.current;

    setIsLoading(true);

    const fetchData = async () => {
      try {
        const params: {
          page: number;
          page_size: number;
          search?: string;
          status?: 'in_pending' | 'paid';
          created_at__start?: string;
          created_at__end?: string;
        } = {
          page: currentPage,
          page_size: pageSize,
        };

        // Search via API
        if (filters.search) {
          params.search = filters.search;
        }

        // Map status filter
        if (filters.status) {
          params.status = filters.status as 'in_pending' | 'paid';
        }

        // Apply period filter (date range)
        if (filters.period) {
          const dateRange = getDateRangeFromPeriod(filters.period);
          if (dateRange.start) params.created_at__start = dateRange.start;
          if (dateRange.end) params.created_at__end = dateRange.end;
        }

        // Apply limit filter (overrides period if set)
        if (filters.limit && filters.limit !== 'all') {
          const dateRange = getDateRangeFromLimit(filters.limit);
          if (dateRange.start) params.created_at__start = dateRange.start;
          if (dateRange.end) params.created_at__end = dateRange.end;
        }

        const response = await partnerRevshareService.getCosts(params);

        // Only update state if this is still the current request
        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        // Transform API response to UI format
        // API returns { page, page_size, count, objects, total_amount }
        const transformedCosts = response.objects.map((cost) => transformCostData(cost, t, lang));

        setCosts(transformedCosts);
        setTotalCount(response.count);
        setTotalAmount(response.total_amount ? parseFloat(response.total_amount) : 0);
      } catch (error: any) {
        // Only handle error if this is still the current request
        if (requestIdRef.current !== currentRequestId) {
          return;
        }
        console.error('Error fetching costs:', error);
        setCosts([]);
        setTotalCount(0);
      } finally {
        // Only update loading state if this is still the current request
        if (requestIdRef.current === currentRequestId) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    // Store the fetch function for external use (e.g., after modal submit)
    fetchCostsRef.current = fetchData;
  }, [currentPage, pageSize, filters, getDateRangeFromPeriod, getDateRangeFromLimit, t, lang]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Debounce search - only update if value actually changed, skip on initial mount
  useEffect(() => {
    // Skip debounce on initial mount to prevent double render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const timeoutId = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search !== localSearch) {
          return { ...prev, search: localSearch };
        }
        return prev;
      });
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timeoutId);
  }, [localSearch]);

  const handleFilterChange = useCallback((key: keyof CostsFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleNewCost = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleSubmitCost = useCallback(() => {
    // Refresh costs list after successful submission
    fetchCostsRef.current();
  }, []);

  return (
    <div className="w-full px-4 py-6 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <Typography variant="h4" className="mb-2">
          {t('partner.revshare.costs.title')}
        </Typography>
      </div>
      {/* Filters */}
      <div className=" mb-4">
        <FiltersSection
          filters={filters}
          localSearch={localSearch}
          isLoading={isLoading}
          hasActiveFilters={hasActiveFilters}
          onSearchChange={setLocalSearch}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          onNewCost={handleNewCost}
        />
      </div>
      {/* Table */}
      <CostsTable
        costs={costs}
        isLoading={isLoading}
        totalCount={totalCount}
        totalAmount={totalAmount}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
      {/* New Cost Modal */}
      <NewCostModal isOpen={isModalOpen} onClose={handleCloseModal} onSubmit={handleSubmitCost} />
    </div>
  );
};

export default RevshareCosts;
